import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { AnalyticSearchRequestDto, ChartResponseDto, InvoiceChargeSortDto } from 'src/generated/services/TFinancialApi';
import { ChargeColumnHeader } from './ChargeColumnHeader';
import { TableDetail } from './TableDetail';
import { useTableFilterState } from './hooks/useTableFilterState';
import { buildChargeFilterSearchRequest, mergeTableFiltersIntoRequest } from './analyticsSearchRequestBuilders';
import { CHARGE_SORTABLE_COLUMNS } from './constants/chargeColumnConfig';
import { getChargesColumns, TableTab } from './useTable';
import { ChargeTableFilters, UnifiedAnalyticsFilters } from './analyticsTableFilterTypes';
import { useAnalyticsData } from '../context/AnalyticsDataContext';
import { useGetInvoiceChargesData } from './useTableData';
import { useColumnVisibility } from '../../../molecules/table/columnSettings';

// Mapping for charge field names to field IDs
const chargeFieldNameMap: Record<string, string> = {
  shipmentNumber: 'SHIPMENT_NUMBER',
  invoiceNumber: 'INVOICE_NUMBER',
  invoiceDate: 'INVOICE_DATE',
  chargeDomain: 'CHARGE_DOMAIN',
  chargeName: 'CHARGE_NAME',
  chargeAmount: 'CHARGE_AMOUNT',
  chargeQuantity: 'CHARGE_QUANTITY',
  chargeUnitPrice: 'CHARGE_UNIT_PRICE',
  exchangeRate: 'EXCHANGE_RATE',
  serviceProvider: 'SERVICE_PROVIDER_NAME',
  serviceType: 'SERVICE',
  modality: 'MODALITY',
  pol: 'POL',
  pod: 'POD',
  legType: 'LEG_TYPE',
  billedEntity: 'BILLED_ENTITY',
  chargeStatus: 'CHARGE_STATUS',
  chargeStatusReason: 'CHARGE_STATUS_REASON',
  deviationAmountInInvoiceCurrency: 'DEVIATION_AMOUNT_INVOICE_CURRENCY',
  chargeAmountInInvoiceCurrency: 'CHARGE_AMOUNT_INVOICE_CURRENCY',
  chargeAmountInReferenceCurrency: 'CHARGE_AMOUNT_REFERENCE_CURRENCY',
  quantityUnit: 'QUANTITY_UNIT',
  date: 'DATE_REF',
};

interface ChargeTableProps {
  chartData: ChartResponseDto;
  page: { pageNumber: number; pageSize: number };
  initialSearchRequest: AnalyticSearchRequestDto;
  isTabVisited: boolean;
  dateReference: string;
  currentCurrency: string;
  sharedFilters?: UnifiedAnalyticsFilters;
  onFilterChange?: (columnId: string, selectedValues: string[]) => void;
}

export const ChargeTable: React.FC<ChargeTableProps> = ({
  chartData,
  page,
  initialSearchRequest,
  isTabVisited,
  dateReference,
  currentCurrency,
  sharedFilters,
  onFilterChange,
}) => {
  // Access context for updating totals and drawer filters
  const { updateTotal, drawerFilters } = useAnalyticsData();
  // Sorting state
  const [sortingField, setSortingField] = useState<InvoiceChargeSortDto['field']>('INVOICE_NUMBER');
  const [sortingMethod, setSortingMethod] = useState<string>('ASC');

  // Filter state management
  const { tableFilters, filterSelections, handleColumnFilter } = useTableFilterState<ChargeTableFilters>({
    initialFilters: {
      // Cross-filter fields (shared across all tables)
      shipmentNumber: [],
      invoiceNumber: [],
      serviceProviderCode: [],
      modality: [],
      billedEntity: [],
      // Charge-specific fields
      pol: [],
      pod: [],
      chargeDomain: [],
      chargeName: [],
      legType: [],
      quantityUnit: [],
      chargeStatus: [],
      chargeStatusReason: [],
      serviceType: [],
      chargeCount: [],
    },
    externalFilters: sharedFilters as unknown as ChargeTableFilters,
    onExternalFilterChange: onFilterChange,
    filterToSelectionMap: (filters) => ({
      // Cross-filter fields
      shipmentNumber: filters.shipmentNumber ?? [],
      invoiceNumber: filters.invoiceNumber ?? [],
      serviceProvider: filters.serviceProviderCode ?? [],
      modality: filters.modality ?? [],
      billedEntity: filters.billedEntity ?? [],
      // Charge-specific fields
      pol: filters.pol ?? [],
      pod: filters.pod ?? [],
      chargeDomain: filters.chargeDomain ?? [],
      chargeName: filters.chargeName ?? [],
      legType: filters.legType ?? [],
      chargeStatus: filters.chargeStatus ?? [],
      chargeStatusReason: filters.chargeStatusReason ?? [],
      quantityUnit: filters.quantityUnit ?? [],
      serviceType: filters.serviceType ?? [],
    }),
    columnIdToFilterKey: (columnId) => {
      if (columnId === 'serviceProvider') return 'serviceProviderCode';
      return columnId;
    },
  });

  const searchRequest = useMemo(
    () => mergeTableFiltersIntoRequest(initialSearchRequest, tableFilters as unknown as UnifiedAnalyticsFilters, 'charge'),
    [initialSearchRequest, tableFilters]
  );

  // Data fetching
  const {
    data: chargesData,
    refetch: refetchData,
    isLoading,
  } = useGetInvoiceChargesData(
    {
      page,
      searchRequest,
      sorting: {
        field: sortingField,
        direction: sortingMethod as InvoiceChargeSortDto['direction'],
      },
    },
    isTabVisited
  );

  // Column configuration
  const columns = useMemo(() => getChargesColumns({ dateReference, currentCurrency }), [dateReference, currentCurrency]);
  // Column visibility management
  const { columns: columnConfigs } = useColumnVisibility();

  // Sorting handler
  const handleSort = useCallback(
    (method: string, fieldName: string) => {
      setSortingMethod(method);
      const fieldId = fieldName === 'date' ? dateReference : chargeFieldNameMap[fieldName] || 'INVOICE_NUMBER';
      setSortingField(fieldId as InvoiceChargeSortDto['field']);
    },
    [dateReference]
  );

  // Enhanced columns with Charge headers
  const enhancedColumns = useMemo(() => {
    // Create a map of default columns for easy lookup
    const defaultColumnsMap = new Map(columns.map((col) => [col.accessorKey as string, col]));

    // Use columnConfigs to determine order and visibility
    return columnConfigs
      .filter((config) => config.isVisible && defaultColumnsMap.has(config.id))
      .map((config) => {
        const col = defaultColumnsMap.get(config.id);
        const accessorKey = col.accessorKey as string;

        if (!accessorKey || !CHARGE_SORTABLE_COLUMNS.has(accessorKey)) {
          return col;
        }

        const customHeader = col.Header || col.header;

        // Map accessorKey to drawer filter key
        const getDrawerFilterKey = (key: string): keyof typeof drawerFilters | null => {
          switch (key) {
            case 'serviceProvider':
              return 'serviceProviderCode';
            default:
              return key as keyof typeof drawerFilters;
          }
        };

        const drawerFilterKey = getDrawerFilterKey(accessorKey);
        const drawerFilterValue = drawerFilterKey ? drawerFilters[drawerFilterKey] ?? [] : [];

        return {
          ...col,
          Header: ({ column }) => (
            <ChargeColumnHeader
              column={column}
              accessorKey={accessorKey}
              filterSearchRequest={buildChargeFilterSearchRequest(accessorKey, searchRequest, tableFilters)}
              initialSelectedValues={filterSelections[accessorKey] ?? []}
              drawerFilterValue={drawerFilterValue}
              onFilter={handleColumnFilter}
              onSort={handleSort}
              customHeader={customHeader}
            />
          ),
        };
      });
  }, [columns, columnConfigs, searchRequest, tableFilters, filterSelections, handleColumnFilter, handleSort, drawerFilters]);

  const tableDetail = {
    columns: enhancedColumns,
    data: chargesData?.invoiceCharges,
    total: chargesData?.total,
    page: chargesData?.page,
    isLoading: isTabVisited && (isLoading || !chargesData),
    isTabVisited,
  };

  return (
    <TableDetail
      tableDetail={tableDetail}
      chartData={chartData}
      page={page}
      tableTab={TableTab.CHARGES}
      refetchData={refetchData}
      onSorting={() => {}} // No-op: ChargeTable handles sorting internally via ChargeColumnHeader
    />
  );
};
